import { useState, useEffect, useRef } from "react";
import { Link } from "@/lib/router-compat";
import { NavLink } from "@/components/NavLink";
import { Menu, ChevronDown, ExternalLink, Phone } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { SheetClose } from "@/components/ui/sheet";
import { lazy, Suspense } from "react";
const QuoteModal = lazy(() =>
  import("./QuoteModal").then((m) => ({ default: m.QuoteModal })),
);
const MobileMenu = lazy(() =>
  import("./MobileMenu").then((m) => ({ default: m.MobileMenu })),
);
import { areas } from "@/data/areas";

interface NavItem {
  label?: string;
  href?: string;
  subItems?: NavItem[];
  isSeparator?: boolean;
}

const servicesItems: NavItem[] = [
  { label: "View All Services", href: "/services" },
  { label: "Pool Remodeling & Resurfacing", href: "/services/pool-remodeling" },
  { label: "Pool Deck Remodeling", href: "/services/pool-deck-remodeling" },
  { label: "Driveway & Patio Pavers", href: "/services/driveway-patio-pavers" },
  { label: "Outdoor Enhancements", href: "/services/outdoor-enhancements" },
  { label: "Fencing", href: "/services/fencing" },
];

const materialsItems: NavItem[] = [
  { label: "Brick Pavers", href: "/brick-pavers" },
  { label: "Marble Pavers", href: "/marble-pavers" },
  { label: "Travertine Pavers", href: "/travertine-pavers" },
  { label: "Glass Tile Mosaics", href: "/glass-tile-mosaics" },
];

const areasItems: NavItem[] = [
  { label: "View All Areas", href: "/areas" },
  { isSeparator: true },
  {
    label: "Broward County",
    href: "/areas/broward-county",
    subItems: [
      { label: "Fort Lauderdale", href: "/areas/fort-lauderdale" },
      { label: "Coral Springs", href: "/areas/coral-springs" },
      { label: "Pembroke Pines", href: "/areas/pembroke-pines" },
      { label: "Weston", href: "/areas/weston" },
      { label: "Miramar", href: "/areas/miramar" },
      { label: "Sunrise", href: "/areas/sunrise" },
      { label: "Davie", href: "/areas/davie" },
    ],
  },
  {
    label: "Miami-Dade County",
    href: "/areas/miami-dade-county",
    subItems: [
      { label: "Miami", href: "/areas/miami" },
      { label: "Coral Gables", href: "/areas/coral-gables" },
      { label: "Brickell", href: "/areas/brickell" },
      { label: "Coconut Grove", href: "/areas/coconut-grove" },
    ],
  },
  {
    label: "Palm Beach County",
    href: "/areas/palm-beach-county",
    subItems: [
      { label: "Boca Raton", href: "/areas/boca-raton" },
      { label: "Delray Beach", href: "/areas/delray-beach" },
      { label: "Boynton Beach", href: "/areas/boynton-beach" },
      { label: "Wellington", href: "/areas/wellington" },
    ],
  },
  { isSeparator: true },
  { label: "Orlando", href: "/areas/orlando" },
  { label: "Tampa", href: "/areas/tampa" },
];

function SubDropdown({
  item,
  setIsOpen,
}: {
  item: NavItem;
  setIsOpen: (val: boolean) => void;
}) {
  const [isSubOpen, setIsSubOpen] = useState(false);
  const enterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
    enterTimeout.current = setTimeout(() => setIsSubOpen(true), 150);
  };

  const handleMouseLeave = () => {
    if (enterTimeout.current) clearTimeout(enterTimeout.current);
    leaveTimeout.current = setTimeout(() => setIsSubOpen(false), 300);
  };

  const hasSubItems = item.subItems && item.subItems.length > 0;

  return (
    <div
      className="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-popover-foreground hover:bg-secondary/10 hover:text-secondary ${isSubOpen ? "bg-secondary/10 text-secondary" : ""}`}
      >
        <NavLink
          to={item.href || "#"}
          end
          onClick={() => setIsOpen(false)}
          className="flex-1"
        >
          {item.label}
        </NavLink>
        {hasSubItems && (
          <ChevronDown
            className={`h-4 w-4 -rotate-90 transition-transform duration-200 ${isSubOpen ? "rotate-0" : ""}`}
          />
        )}
      </div>
      <AnimatePresence>
        {isSubOpen && hasSubItems && (
          <m.div
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute top-0 left-[calc(100%+0.5rem)] w-64 rounded-xl border bg-popover p-2 shadow-xl"
          >
            {item.subItems!.map((subItem, idx) => (
              <NavLink
                key={subItem.href || idx}
                to={subItem.href || "#"}
                end
                onClick={() => {
                  setIsSubOpen(false);
                  setIsOpen(false);
                }}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary outline-hidden ${
                    isActive
                      ? "bg-secondary/10 text-secondary"
                      : "text-popover-foreground hover:bg-secondary/10 hover:text-secondary"
                  }`
                }
              >
                {subItem.label}
              </NavLink>
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NavDropdown({
  label,
  items,
  isScrolled,
}: {
  label: string;
  items: NavItem[];
  isScrolled: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const enterTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
    enterTimeout.current = setTimeout(() => setIsOpen(true), 150);
  };

  const handleMouseLeave = () => {
    if (enterTimeout.current) clearTimeout(enterTimeout.current);
    leaveTimeout.current = setTimeout(() => setIsOpen(false), 300);
  };

  return (
    <div
      className="relative group h-full flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={label}
        className={`flex items-center gap-1 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden py-2 whitespace-nowrap ${
          isScrolled
            ? "text-foreground/80 hover:text-secondary"
            : "text-white/90 hover:text-white"
        }`}
      >
        {label}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute top-full left-0 mt-2 w-64 rounded-xl border bg-popover p-2 shadow-xl"
          >
            {items.map((item, index) => {
              if (item.isSeparator) {
                return (
                  <div
                    key={`sep-${index}`}
                    className="my-1.5 h-px bg-border/60 mx-2"
                  />
                );
              }

              if (item.subItems) {
                return (
                  <SubDropdown
                    key={item.href || index}
                    item={item}
                    setIsOpen={setIsOpen}
                  />
                );
              }

              const isArea =
                item.href &&
                item.href.startsWith("/areas/") &&
                item.href !== "/areas";
              const slug = item.href?.split("/").pop();
              const area = isArea ? areas.find((a) => a.slug === slug) : null;

              return (
                <NavLink
                  key={item.href || index}
                  to={item.href || "#"}
                  end
                  onClick={() => setIsOpen(false)}
                  onMouseEnter={() => {
                    if (isArea && slug) {
                      const link = document.createElement("link");
                      link.rel = "preload";
                      link.as = "image";
                      link.href = area?.image || `/images/areas/${slug}.jpg`;
                      document.head.appendChild(link);
                    }
                  }}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary outline-hidden ${
                      isActive
                        ? "bg-secondary/10 text-secondary"
                        : "text-popover-foreground hover:bg-secondary/10 hover:text-secondary"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileCollapsibleItem({ item }: { item: NavItem }) {
  const [isOpen, setIsOpen] = useState(false);

  if (item.subItems && item.subItems.length > 0) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between py-2 text-sm font-medium text-foreground hover:text-secondary transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-secondary rounded-xs">
          <SheetClose asChild>
            <NavLink to={item.href || "#"} end className="flex-1">
              {item.label}
            </NavLink>
          </SheetClose>
          <button onClick={() => setIsOpen(!isOpen)} className="p-1">
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
        <AnimatePresence>
          {isOpen && (
            <m.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-col pl-4 py-1 gap-2 border-l-2 border-muted ml-2 mb-2">
                {item.subItems.map((subItem, idx) => (
                  <SheetClose asChild key={subItem.href || idx}>
                    <NavLink
                      to={subItem.href || "#"}
                      end
                      className={({ isActive }) =>
                        `text-sm font-medium transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-secondary rounded-xs ${
                          isActive
                            ? "text-secondary"
                            : "text-muted-foreground hover:text-secondary"
                        }`
                      }
                    >
                      {subItem.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <SheetClose asChild>
      <NavLink
        to={item.href || "#"}
        end
        className={({ isActive }) =>
          `py-2 text-sm font-medium transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-secondary rounded-xs ${
            isActive
              ? "text-secondary"
              : "text-muted-foreground hover:text-secondary"
          }`
        }
      >
        {item.label}
      </NavLink>
    </SheetClose>
  );
}

export function MobileCollapsible({
  label,
  items,
}: {
  label: string;
  items: NavItem[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex flex-col">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between py-2 text-lg font-medium text-foreground hover:text-secondary transition-colors outline-hidden focus-visible:ring-2 focus-visible:ring-secondary rounded-xs"
      >
        {label}
        <ChevronDown
          className={`h-5 w-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col pl-4 py-2 gap-3 border-l-2 border-muted ml-2 mb-2">
              {items.map((item, index) => {
                if (item.isSeparator) {
                  return (
                    <div
                      key={`sep-${index}`}
                      className="my-1 h-px bg-border/60 mr-4"
                    />
                  );
                }
                return (
                  <MobileCollapsibleItem key={item.href || index} item={item} />
                );
              })}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${
        isScrolled
          ? "bg-background/95 backdrop-blur-md shadow-xs border-border"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="relative flex items-center h-16 w-[240px] focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden"
        >
          {/* Light Logo (Transparent Header) */}
          <img
            src="https://vibe.filesafe.space/1779750798598342338/attachments/eeb8548d-48be-486d-aa64-7c3a5e6b3e98.webp"
            alt="Precision Pavers & Pools"
            className={`absolute left-0 top-0 h-16 w-auto object-contain transition-opacity duration-300 ${isScrolled ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          />
          {/* Dark Logo (Scrolled Header) - Temporary brightness filter until dark logo is uploaded */}
          <img
            src="https://vibe.filesafe.space/1779750798598342338/attachments/eeb8548d-48be-486d-aa64-7c3a5e6b3e98.webp"
            alt="Precision Pavers & Pools"
            className={`absolute left-0 top-0 h-16 w-auto object-contain transition-opacity duration-300 brightness-0 ${isScrolled ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          />
        </Link>

        {/* Desktop Nav */}
        <nav
          aria-label="Main navigation"
          className="hidden lg:flex items-center gap-6 h-full whitespace-nowrap"
        >
          <NavDropdown
            label="Services"
            items={servicesItems}
            isScrolled={isScrolled}
          />
          <NavDropdown
            label="Materials"
            items={materialsItems}
            isScrolled={isScrolled}
          />
          <NavDropdown
            label="Areas We Serve"
            items={areasItems}
            isScrolled={isScrolled}
          />

          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden py-2 whitespace-nowrap ${
                isActive
                  ? "text-secondary"
                  : isScrolled
                    ? "text-foreground/80 hover:text-secondary"
                    : "text-white/90 hover:text-white"
              }`
            }
          >
            Projects
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden py-2 whitespace-nowrap ${
                isActive
                  ? "text-secondary"
                  : isScrolled
                    ? "text-foreground/80 hover:text-secondary"
                    : "text-white/90 hover:text-white"
              }`
            }
          >
            About Us
          </NavLink>
          <NavLink
            to="/blog"
            className={({ isActive }) =>
              `text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden py-2 whitespace-nowrap ${
                isActive
                  ? "text-secondary"
                  : isScrolled
                    ? "text-foreground/80 hover:text-secondary"
                    : "text-white/90 hover:text-white"
              }`
            }
          >
            Blog
          </NavLink>
          <a
            href="https://showroom.precisionpaverspools.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-secondary rounded-xs outline-hidden py-2 whitespace-nowrap ${
              isScrolled
                ? "text-foreground/80 hover:text-secondary"
                : "text-white/90 hover:text-white"
            }`}
          >
            Visit Showroom
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </nav>

        {/* Desktop CTAs & Mobile Menu Trigger */}
        <div className="flex items-center gap-4 lg:ml-6">
          <div className="hidden lg:flex items-center gap-4">
            <Button
              variant="outline"
              className={
                isScrolled
                  ? "border-foreground text-foreground hover:bg-accent hover:text-accent-foreground"
                  : "border-white/50 text-white hover:bg-white/10 hover:text-white bg-transparent backdrop-blur-xs"
              }
              asChild
            >
              <a
                href="tel:+19542003501"
                className="flex items-center gap-2 font-semibold"
              >
                <Phone className="h-4 w-4" />
                +1 954-200-3501
              </a>
            </Button>
            <Suspense
              fallback={
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Get a Free Estimate
                </Button>
              }
            >
              <QuoteModal>
                <Button className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Get a Free Estimate
                </Button>
              </QuoteModal>
            </Suspense>
          </div>

          {/* Mobile Menu */}
          <Suspense
            fallback={
              <Button
                variant="ghost"
                size="icon"
                className={`lg:hidden ${isScrolled ? "text-foreground" : "text-white"}`}
              >
                <Menu className="w-6 h-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            }
          >
            <MobileMenu
              isScrolled={isScrolled}
              servicesItems={servicesItems}
              materialsItems={materialsItems}
              areasItems={areasItems}
              MobileCollapsible={MobileCollapsible}
            />
          </Suspense>
        </div>
      </div>
    </header>
  );
}
